import projects from "./projects.json";
import projectContent from "./projectContent.json";
import projectSessions from "./projectSessions.json";

const apiBase = (process.env.REACT_APP_API_BASE_URL ||
  "https://us-central1-openscopedataback.cloudfunctions.net/app/api/table").replace(/\/$/, "");

function asText(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(asText).join(", ");
  if (typeof value === "object") return Object.values(value).map(asText).join(", ");
  return String(value);
}

export function searchPublicSessions(sessions, query) {
  const term = query.trim().toLowerCase();
  return sessions.filter(session => Object.values(session).some(value => asText(value).toLowerCase().includes(term)));
}

export function normalizePublicSessions(payload) {
  if (payload === null) return [];
  if (!payload || typeof payload !== "object") throw new Error("Invalid public session response");

  return Object.entries(payload).filter(([, record]) => record !== null).map(([key, record]) => {
    if (!record || typeof record !== "object" || Array.isArray(record)) {
      throw new Error("Invalid public session record");
    }
    const session = { ...record };
    session.sessionID = asText(record.session_id ?? record.id ?? key);
    session.specimen = asText(record.specimen ?? record.specimen_id);
    session.status = asText(record.status && typeof record.status === "object" ? record.status.status : record.status);
    session.probes = Array.isArray(record.probes)
      ? record.probes.map(probe => asText(probe).replace(/^probe[_ -]?/i, "")).join(", ")
      : asText(record.probes);
    ["mouse", "date", "name", "planes", "files", "dandi", "genotype", "notes", "flags", "fails", "overrides", "flag_notes", "override_notes"].forEach(field => {
      session[field] = asText(record[field]);
    });
    return session;
  });
}

export function countPublicSessions(sessions) {
  const subjects = sessions.map(session => session.mouse.trim());
  return {
    mice: subjects.some(subject => !subject) ? null : new Set(subjects).size,
    sessions: new Set(sessions.map(session => session.sessionID.trim())).size
  };
}

function getSessionSnapshot(project) {
  const snapshot = projectSessions.projects[project.id];
  if (!snapshot || snapshot.dandiset !== project.dandiset) {
    throw new Error("Project session snapshot unavailable or out of date");
  }
  return snapshot;
}

export async function loadProjectCounts(projectId, signal) {
  const project = projects.find(entry => entry.id === projectId);
  if (!project) throw new Error("Unknown project");
  if (project.firebaseProject && project.sessionSource !== "dandi") {
    const records = await fetchJson(`public/${encodeURIComponent(project.firebaseProject)}`, signal);
    return countPublicSessions(normalizePublicSessions(records));
  }
  const snapshot = getSessionSnapshot(project);
  return countPublicSessions(normalizePublicSessions(snapshot.sessions));
}

async function fetchJson(route, signal) {
  const response = await fetch(`${apiBase}/${route}`, { signal });
  if (!response.ok) throw new Error(`Request failed (HTTP ${response.status})`);
  return response.json();
}

async function optionalMetadata(route, signal) {
  try {
    const payload = await fetchJson(route, signal);
    return payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
  } catch (error) {
    if (error.name === "AbortError") throw error;
    return {};
  }
}

export async function loadPublicProject(project, signal) {
  if (!project || !projects.some(entry => entry.firebaseProject === project)) {
    throw new Error("Unknown public project");
  }
  const identifier = encodeURIComponent(project);
  const [records, description, definitions] = await Promise.all([
    fetchJson(`public/${identifier}`, signal),
    optionalMetadata(`disc/${identifier}`, signal),
    optionalMetadata("tags/project", signal)
  ]);
  const sessions = normalizePublicSessions(records);
  return {
    sessions,
    description: {
      Type: sessions.some(session => session.planes) ? "Ophys" : "Ephys",
      ...description
    },
    definitions
  };
}

export async function loadProject(projectId, signal) {
  const project = projects.find(entry => entry.id === projectId);
  if (!project) throw new Error("Unknown project");
  const content = projectContent[projectId] || {};
  if (project.firebaseProject && project.sessionSource !== "dandi") {
    const result = await loadPublicProject(project.firebaseProject, signal);
    return { ...result, description: { ...content, ...result.description }, source: "Firebase public sessions" };
  }
  const snapshot = getSessionSnapshot(project);
  let sessions = normalizePublicSessions(snapshot.sessions);
  let description = { Credits: project.name, ...content };
  let definitions = {};
  if (project.firebaseProject) {
    const legacy = await loadPublicProject(project.firebaseProject, signal);
    description = { ...description, ...legacy.description };
    definitions = legacy.definitions;
    const notesBySession = new Map(legacy.sessions.map(session => [session.sessionID, session]));
    sessions = sessions.map(session => {
      const previous = notesBySession.get(session.sessionID);
      if (!previous) return session;
      if (previous.mouse.trim() !== session.mouse.trim()) {
        throw new Error("Conflicting mouse identity for curated session notes");
      }
      return { ...session, notes: previous.notes };
    });
  }
  return {
    sessions,
    description,
    definitions,
    source: `DANDI ${snapshot.dandiset} / ${snapshot.version}`,
    retrievedAt: snapshot.generatedAt || projectSessions.generatedAt,
    draft: snapshot.version === "draft"
  };
}